import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { CheckCircleIcon, ProhibitIcon } from '@phosphor-icons/react';
import IconWrapper from '@components/ui/Icons';

import { getStockTierFromQuantity, type StockTier } from '../getStockTier';

import styles from './StoreItem.module.scss';

export type PdpStoreItemProps = {
  storeName: string;
  detailsLine: string;
  /** Raw storefront quantity; null/undefined shows availability-unavailable fallback. */
  quantity: number | null | undefined;
  className?: string;
};

const PdpStoreItem = ({
  storeName,
  detailsLine,
  quantity,
  className,
}: PdpStoreItemProps) => {
  const { t } = useTranslation('pdp');
  const tier = getStockTierFromQuantity(quantity);
  const count = quantity ?? 0;

  const contentByTier: Record<
    StockTier,
    { statusText: string; srPrefix: string | null }
  > = {
    in_stock: {
      statusText: t('product.findInStore.inStockCount', { count }),
      srPrefix: null,
    },
    few_left: {
      statusText: t('product.findInStore.fewLeftCount', { count }),
      srPrefix: t('product.findInStore.srFewLeft', { count }),
    },
    out_of_stock: {
      statusText: t('product.findInStore.outOfStock'),
      srPrefix: t('product.findInStore.srOutOfStock'),
    },
    unknown: {
      statusText: t('product.findInStore.availabilityUnavailable'),
      srPrefix: null,
    },
  };

  const { statusText, srPrefix } = contentByTier[tier];

  return (
    <li className={cn(styles.item, className)}>
      <div className={styles.row}>
        <div className={styles.copy}>
          <p
            className={cn(styles.storeName, {
              [styles.outOfStock]: tier === 'out_of_stock',
            })}
          >
            {storeName}
          </p>
          {detailsLine ? (
            <p
              className={cn(styles.details, {
                [styles.outOfStock]: tier === 'out_of_stock',
              })}
            >
              {detailsLine}
            </p>
          ) : null}
        </div>
        <div className={styles.status}>
          {tier === 'in_stock' ? (
            <CheckCircleIcon
              className={styles.icon}
              size={20}
              weight="fill"
              color="var(--primary-500)"
            />
          ) : null}
          {tier === 'few_left' ? (
            <IconWrapper
              classNames={styles.icon}
              icon="fire"
              height={20}
              fill="var(--accents-popular-500)"
              width={20}
            />
          ) : null}
          {tier === 'out_of_stock' ? (
            <ProhibitIcon
              className={styles.icon}
              size={20}
              color="var(--accents-sale-500)"
              weight="bold"
            />
          ) : null}
          {srPrefix ? (
            <span className={styles.srOnly}>{`${srPrefix} `}</span>
          ) : null}
          <p className={styles.statusText}>{statusText}</p>
        </div>
      </div>
    </li>
  );
};

export default PdpStoreItem;
